import axios from 'axios';
import { ServiceRemidnerFull, ServiceReminderData } from '../pages/ServiceReminders/serviceReminders.types';
import { dropDown } from './interfaces';

const apiURL = process.env.REACT_APP_API_URL;

/**
 * Retrieves the service reminder grid data from the server.
 * @returns A promise that resolves to an array of ServiceReminderData objects.
 * @throws An error if the authorization token is not found.
 */

export const getServiceReminderGrid = async (
  showCompleted: boolean,
  showDeleted: boolean,
  serviceTypeId: number = 0
): Promise<ServiceReminderData[]> => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('Authorization token not found');
  }

  // Build params object
  const params: Record<string, any> = {
    ShowCompleted: showCompleted,
    ShowDeleted: showDeleted,
  };
  // Only include the filter when it's non‐zero
  if (serviceTypeId > 0) {
    params.ServiceTypeId = serviceTypeId;
  }

  const response = await axios.get(
    `https://${apiURL}/ServiceReminder`,
    {
      params,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data as ServiceReminderData[];
};


/**
 * Retrieves a service reminder by its ID.
 * @param id - The ID of the service reminder.
 * @returns A promise that resolves to the service reminder data.
 * @throws An error if the authorization token is not found.
 */
export const getServiceReminderById = async (id: number): Promise<ServiceRemidnerFull> => {
   const token = localStorage.getItem('authToken');

   if (!token) {
      throw new Error('Authorization token not found');
   }

   const response = await axios.get(`https://${apiURL}/ServiceReminder/${id}`, {
      headers: {
         'Content-Type': 'application/json',
         'Authorization': 'Bearer ' + token,
      }
   });

   const data: ServiceRemidnerFull = response.data;
   return data;
}

interface UpdateServiceReminder {
   serviceReminderId: number;
   clientId: number;
   siteId: number;
   workTypeId: number;
   description: string;
   dueDate: string;
   notify1DateTime: string;
   notify2DateTime: string;
   notify3DateTime: string;
   employeeAssignedtoId: number;
   serviceTypes: { serviceTypeId: number }[];
}

/**
 * Updates a service reminder.
 * @param props - The properties for updating the service reminder.
 * @returns A promise that resolves to the updated service reminder.
 * @throws An error if the authorization token is not found.
 */
export const updateServiceReminder = async (props: UpdateServiceReminder): Promise<ServiceRemidnerFull> => {
   const token = localStorage.getItem('authToken');

   if (!token) {
      throw new Error('Authorization token not found');
   }

   const response = await axios.put(`https://${apiURL}/ServiceReminder/${props.serviceReminderId}`, props, {
      headers: {
         'Content-Type': 'application/json',
         'Authorization': 'Bearer ' + token,
      }
   });

   const data: ServiceRemidnerFull = response.data;
   return data;
}

interface CreateServiceReminder {
   clientId: number;
   siteId: number;
   workTypeId: number;
   description: string;
   dueDate: string;
   notify1DateTime: string;
   notify2DateTime: string;
   notify3DateTime: string;
   employeeAssignedtoId: number;
   status: number;
   serviceTypes: { serviceTypeId: number }[];
}

/**
 * Creates a service reminder.
 * @param props - The properties for creating the service reminder.
 * @returns A Promise that resolves to void.
 * @throws An error if the authorization token is not found.
 */
export const createServiceReminder = async (props: CreateServiceReminder): Promise<void> => {
   const token = localStorage.getItem('authToken');

   if (!token) {
      throw new Error('Authorization token not found');
   }

   const response = await axios.post(`https://${apiURL}/ServiceReminder/`, props, {
      headers: {
         'Content-Type': 'application/json',
         'Authorization': 'Bearer ' + token,
      }
   });
   console.log(response)
}

/**
 * Deletes a service reminder by its ID.
 * @param id - The ID of the service reminder to delete.
 * @returns A Promise that resolves to void.
 * @throws An error if the authorization token is not found.
 */
export const deleteServiceReminder = async (id: number, jobid: number): Promise<void> => {
   const token = localStorage.getItem('authToken');

   if (!token) {
      throw new Error('Authorization token not found');
   }

   const response = await axios.put(`https://${apiURL}/ServiceReminder/MarkAsDeleted/${id}/${jobid}`, {}, {
      headers: {
         'Content-Type': 'application/json',
         'Authorization': 'Bearer ' + token,
      }
   });
   console.log(response)
}


/**
 * Marks a service reminder as complete.
 * @param id - The ID of the service reminder to mark as complete.
 * @returns A Promise that resolves to void.
 * @throws Error if the authorization token is not found.
 */
export const completeServiceReminder = async (id: number, renew: boolean): Promise<void> => {
   const token = localStorage.getItem('authToken');

   if (!token) {
      throw new Error('Authorization token not found');
   }

   const response = await axios.put(`https://${apiURL}/ServiceReminder/MarkAsComplete/${id}?renew=${renew}`, {}, {
      headers: {
         'Content-Type': 'application/json',
         'Authorization': 'Bearer ' + token,
      }
   });
   console.log(response)
}

export interface JobReminder {
      serviceReminderId: number
      created: string
      modified: any
      clientId: number
      client: any
      siteId: number
      workTypeId: number
      workType: any
      description: string
      dueDate: string
      notify1DateTime: string
      notify2DateTime: string
      notify3DateTime: string
      notify1SentDateTime: any
      notify2SentDateTime: any
      notify3SentDateTime: any
      employeeAssignedtoId: number
      employeeAssignedto: any
      employeeCreatedby: any
      status: number
}



/**
 * Retrieves job reminders for a specific client and site.
 * @param clientId - The ID of the client.
 * @param siteId - The ID of the site.
 * @returns A Promise that resolves to the response containing the job reminders.
 * @throws An error if the authorization token is not found.
 */
export const getJobReminders = async (clientId: number, siteId: number): Promise<JobReminder[]> => {
   const token = localStorage.getItem('authToken');

   if (!token) {
      throw new Error('Authorization token not found');
   }

   const response = await axios.get(`https://${apiURL}/ServiceReminder/Client/${clientId}/Site/${siteId}`, {
      headers: {
         'Content-Type': 'application/json',
         'Authorization': 'Bearer ' + token,
      }
   });
   return response.data as JobReminder[];
}

export const getServiceTypes = async (): Promise<{ id: number; name: string }[]> => {
  const token = localStorage.getItem('authToken');

  if (!token) {
    throw new Error('Authorization token not found');
  }

  const response = await axios.get(`https://${apiURL}/ServiceType`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    }
  });

  // Convert API { value, text } to expected { id, name }
  return (response.data as dropDown[]).map(item => ({
    id: item.value,
    name: item.text
  }));
};
